import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { teamViewsAvatarColor } from "./teamViewsHelpers";
import type { AccountColumnPerson } from "./teamViewsHelpers";
import type { PersonCardVariant } from "./TeamViewsPersonCard";
import type { TeamViewsPerson } from "../../../Store/Reducers/TeamViewsSlice";
import { getInitials } from "../../../Utils/Helper/Helper";
import { EXEC_SPONSOR_AVATAR_BG } from "../../../Utils/Helper/profileAvatarUi";

const POP_MARGIN = 8;
/** Matches `.tv-cell-pop` max-width — used for first-paint clamp before measure. */
const POP_ESTIMATED_WIDTH = 280;

type TeamViewsAccountCellProps = {
  people: AccountColumnPerson[];
  /** Column label ("AE", "Sponsor", …) used in the +N popover header. */
  roleLabel: string;
  variant?: PersonCardVariant;
  resolvePerson: (userId?: string | null) => TeamViewsPerson | undefined;
  onSelectPerson: (userId?: string | null) => void;
};

function clampPopoverPosition(
  preferred: { top: number; left: number },
  size: { width: number; height: number },
  trigger: DOMRect
): { top: number; left: number } {
  const maxLeft = Math.max(
    POP_MARGIN,
    window.innerWidth - size.width - POP_MARGIN
  );
  const left = Math.min(Math.max(preferred.left, POP_MARGIN), maxLeft);

  let top = preferred.top;
  if (top + size.height > window.innerHeight - POP_MARGIN) {
    // Flip above the trigger when there isn't room below.
    top = Math.max(POP_MARGIN, trigger.top - size.height - 6);
  }
  return { top, left };
}

/**
 * V3 By Account cell: lead person visible; +N opens a popover for the rest.
 */
const TeamViewsAccountCell: React.FC<TeamViewsAccountCellProps> = ({
  people,
  roleLabel,
  resolvePerson,
  onSelectPerson,
}) => {
  const [popAt, setPopAt] = useState<{ top: number; left: number } | null>(
    null
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!popAt) return undefined;
    const close = () => setPopAt(null);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [popAt]);

  // After paint, re-clamp using the real popover size (right-edge columns).
  useLayoutEffect(() => {
    if (!popAt || !popRef.current || !triggerRef.current) return;
    const popRect = popRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const next = clampPopoverPosition(
      popAt,
      { width: popRect.width, height: popRect.height },
      triggerRect
    );
    if (next.left !== popAt.left || next.top !== popAt.top) {
      setPopAt(next);
    }
  }, [popAt]);

  const avatarBg = (person: AccountColumnPerson): string => {
    const rosterPerson = resolvePerson(person.user_id);
    return rosterPerson
      ? teamViewsAvatarColor(rosterPerson)
      : EXEC_SPONSOR_AVATAR_BG;
  };

  const personKey = (person: AccountColumnPerson): string =>
    person.user_id ? String(person.user_id) : person.full_name;

  const openPop = () => {
    if (popAt) {
      setPopAt(null);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPopAt(
      clampPopoverPosition(
        { top: rect.bottom + 6, left: rect.left },
        { width: POP_ESTIMATED_WIDTH, height: 120 },
        rect
      )
    );
  };

  const renderPerson = (person: AccountColumnPerson) => {
    const rosterPerson = resolvePerson(person.user_id);
    return (
      <button
        key={personKey(person)}
        type="button"
        className="tv-by-account-chip"
        title={
          rosterPerson
            ? `View accounts for ${person.full_name}`
            : person.full_name
        }
        disabled={!rosterPerson}
        onClick={() => {
          setPopAt(null);
          onSelectPerson(person.user_id);
        }}
      >
        <span
          className="tv-cell-avatar"
          style={{ background: avatarBg(person) }}
          aria-hidden
        >
          {getInitials(person.full_name)}
        </span>
        <span className="tv-by-account-name">{person.full_name}</span>
      </button>
    );
  };

  if (!people.length) {
    return <span className="tv-by-account-empty">—</span>;
  }

  if (people.length === 1) {
    return (
      <div className="tv-by-account-chips">{people.map(renderPerson)}</div>
    );
  }

  const [lead, ...rest] = people;
  const popover = popAt
    ? createPortal(
        <div
          ref={popRef}
          className="rl-prototype-skin tv-cell-pop"
          style={{ top: popAt.top, left: popAt.left }}
          role="dialog"
          aria-label={`${roleLabel} on this account`}
        >
          <div className="tv-cell-pop-hdr">{roleLabel}</div>
          <div className="tv-by-account-chips tv-cell-pop-list">
            {rest.map(renderPerson)}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="tv-by-account-chips">
      <div className="tv-cell-lead-row">
        {renderPerson(lead)}
        <button
          ref={triggerRef}
          type="button"
          className="tv-cell-expand"
          aria-expanded={Boolean(popAt)}
          title={rest.map(person => person.full_name).join(", ")}
          onClick={openPop}
        >
          +{rest.length}
        </button>
      </div>
      {popover}
    </div>
  );
};

export default TeamViewsAccountCell;
